import { useContext, useEffect, useRef, useState, type FormEvent } from "react";
import { CMSComponent, PageContextValue, type CMSComponentProps } from "../engine/CMSComponent.tsx";
import RichText from "../engine/RichText.tsx";
import { isRichText } from "../types.ts";
import KitButton from "./KitButton.tsx";
import { SectionShell } from "./Section.tsx";

type Status = "idle" | "sending" | "sent" | "error";

interface Turnstile {
  render: (element: HTMLElement, options: Record<string, unknown>) => string;
  reset: (widgetId?: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

let turnstileLoader: Promise<Turnstile> | undefined;

/**
 * Loads Turnstile once, in explicit mode. Rendering the widget from an effect
 * (instead of the implicit .cf-turnstile scan) keeps its injected iframe from
 * breaking React hydration. A script created by our nonce'd bundle is allowed
 * by the CSP through 'strict-dynamic'.
 */
const loadTurnstile = () =>
  (turnstileLoader ??= new Promise<Turnstile>((resolve, reject) => {
    if (window.turnstile) return resolve(window.turnstile);
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile missing"));
    script.onerror = () => reject(new Error("Turnstile failed to load"));
    document.head.appendChild(script);
  }));

const str = (value: unknown) => (typeof value === "string" ? value : undefined);

export default function ContactForm({ node }: CMSComponentProps) {
  const { turnstileSiteKey } = useContext(PageContextValue);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState("");
  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<string | undefined>(undefined);
  const token = useRef("");

  const sent = status === "sent";

  useEffect(() => {
    if (!turnstileSiteKey || sent || !widgetRef.current) return;
    let cancelled = false;
    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !widgetRef.current) return;
        widgetId.current = turnstile.render(widgetRef.current, {
          sitekey: turnstileSiteKey,
          theme: "auto",
          callback: (value: string) => (token.current = value),
          "expired-callback": () => (token.current = ""),
        });
      })
      .catch((loadError) => console.error("[contact]", loadError));
    return () => {
      cancelled = true;
      if (widgetId.current) window.turnstile?.remove(widgetId.current);
      widgetId.current = undefined;
      token.current = "";
    };
  }, [turnstileSiteKey, sent]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    const form = event.currentTarget;
    const field = (name: string) =>
      (form.elements.namedItem(name) as HTMLInputElement | HTMLTextAreaElement).value;

    if (turnstileSiteKey && !token.current) {
      setError("Please complete the verification and try again.");
      setStatus("error");
      return;
    }

    setStatus("sending");
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: field("name"),
          email: field("email"),
          message: field("message"),
          company: field("company"),
          turnstileToken: token.current,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || "Something went wrong");
      }
      setStatus("sent");
      form.reset();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : "Something went wrong");
      setStatus("error");
      // Turnstile tokens are single use; reset so a retry gets a fresh one
      token.current = "";
      window.turnstile?.reset(widgetId.current);
    }
  }

  const sending = status === "sending";

  return (
    <SectionShell node={node} className="cms-contact">
      <div className="cms-section-heading cms-reveal">
        <CMSComponent node={node.textGroup} />
      </div>

      {sent ? (
        <div className="cms-contact__success" role="status">
          <p className="cms-contact__success-title">{str(node.successTitle)}</p>
          <p className="cms-contact__success-message">{str(node.successMessage)}</p>
        </div>
      ) : (
        <form className="cms-form" onSubmit={handleSubmit}>
          {/* honeypot: hidden from people, filled by bots */}
          <div aria-hidden="true" className="cms-visually-hidden">
            <label htmlFor="company">Company (leave blank)</label>
            <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" />
          </div>

          <div className="cms-form__row">
            <div>
              <label className="cms-form__label" htmlFor="name">
                Name
              </label>
              <input
                className="cms-input"
                id="name"
                name="name"
                type="text"
                required
                maxLength={100}
                disabled={sending}
              />
            </div>
            <div>
              <label className="cms-form__label" htmlFor="email">
                Email
              </label>
              <input
                className="cms-input"
                id="email"
                name="email"
                type="email"
                required
                maxLength={200}
                disabled={sending}
              />
            </div>
          </div>

          <div>
            <label className="cms-form__label" htmlFor="message">
              Message
            </label>
            <textarea
              className="cms-input"
              id="message"
              name="message"
              required
              maxLength={5000}
              rows={5}
              disabled={sending}
            />
          </div>

          {turnstileSiteKey && <div ref={widgetRef} />}

          {status === "error" && (
            <p className="cms-form__error" role="alert">
              {error}
            </p>
          )}

          <div>
            <KitButton type="submit" disabled={sending}>
              {sending ? "Sending..." : str(node.submitLabel) ?? "Send message"}
            </KitButton>
          </div>

          {isRichText(node.consent) && (
            <div className="cms-form__consent">
              <RichText document={node.consent} />
            </div>
          )}
        </form>
      )}
    </SectionShell>
  );
}
